import React, { useMemo } from 'react';
import useEffectOnce from 'react-use/lib/useEffectOnce';
import useApi from '../../_hooks/useApi';
import { urlWithParams } from '../../CommonUtils/urlWithParams';
import { TabHeader } from '../components/TabHeader/TabHeader';
import { TabHeaderLoading } from '../components/TabHeader/TabHeaderLoading';
import { PlanningLabel } from '../components/PlanningLabel/PlanningLabel';

interface Props {
  tabTitle: string;
  mgpItemId: number;
  clientId: string;
  reducerPrefix: string;
  asOfDate: string | null;
  isEmpty: boolean;
  isPristine: boolean;
  LoadingState: JSX.Element;
  EmptyState: JSX.Element;
}

export const Tab: React.FC<Props> = ({
  tabTitle,
  mgpItemId,
  clientId,
  reducerPrefix,
  asOfDate,
  isEmpty,
  isPristine,
  LoadingState,
  EmptyState,
  children,
}) => {
  const [requestReport, { loading: reportLoading, errors: reportErrors }] = useApi(
    urlWithParams('/Consumer/MgpReport/MgpReportItem', { clientId, mgpItemId }),
    {
      reduxActionPrefix: reducerPrefix,
    },
  );

  useEffectOnce(() => {
    if (isPristine) {
      requestReport();
    }
  });

  const TabHeaderComponent = useMemo(
    () => <TabHeader clientId={clientId} title={tabTitle} asOfDate={asOfDate} />,
    [asOfDate, clientId, tabTitle],
  );

  if (reportLoading) {
    return (
      <>
        <TabHeaderLoading title={tabTitle} />
        {LoadingState}
      </>
    );
  }

  if (reportErrors.length > 0) {
    return (
      <>
        {TabHeaderComponent}
        {EmptyState}
        <PlanningLabel />
      </>
    );
  }

  if (isPristine) {
    return null;
  }

  if (isEmpty) {
    return (
      <>
        {TabHeaderComponent}
        {EmptyState}
        <PlanningLabel />
      </>
    );
  }

  return (
    <>
      {TabHeaderComponent}
      {children}
      <PlanningLabel />
    </>
  );
};
